import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FileText, UploadCloud } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useFileDropzone, type DropzoneErrorKind, type DropzoneErrorContext } from '@hooks/useFileDropzone';
import { extensionsFor, labelsFor } from '@constants/mimeLabels';
import { makeDocumentUploadFileSchema } from '@/features/documents/schemas/documentUpload.schema';
import { cn } from '@/lib/utils';

interface DocumentUploadDropzoneProps {
  onFilesSelected: (files: File[]) => void;
  /** From GET /settings/capabilities — the list the route actually enforces. */
  allowedMimeTypes: readonly string[];
  maxFileSize: number;
  maxFiles: number;
}

const DocumentUploadDropzone: React.FC<DocumentUploadDropzoneProps> = ({
  onFilesSelected,
  allowedMimeTypes,
  maxFileSize,
  maxFiles,
}) => {
  const { t } = useTranslation();
  const acceptedFileTypes = useMemo(() => extensionsFor(allowedMimeTypes), [allowedMimeTypes]);
  const formatLabels = useMemo(() => labelsFor(allowedMimeTypes), [allowedMimeTypes]);
  const schema = useMemo(
    () => makeDocumentUploadFileSchema(allowedMimeTypes),
    [allowedMimeTypes],
  );

  const describeError = useCallback(
    (kind: DropzoneErrorKind, context: DropzoneErrorContext) => {
      if (kind === 'count') return t('upload.maxFilesError', { count: context.count });
      if (kind === 'size') return t('upload.fileSizeError', { name: context.name, size: context.maxSizeMb });
      return t('upload.fileTypeError', { name: context.name, types: context.accept });
    },
    [t],
  );

  const [schemaError, setSchemaError] = useState<string | null>(null);

  // Hook checks count/size/extension but never `file.type`, which is what the
  // upload route validates (api/routes/documents.py:271); the schema covers that.
  const acceptSelection = useCallback(
    (files: File[]) => {
      const problems = files.flatMap((file) => {
        const result = schema.safeParse(file);
        return result.success ? [] : result.error.issues.map((entry) => entry.message);
      });

      setSchemaError(problems.length > 0 ? problems.join('; ') : null);
      if (problems.length === 0) onFilesSelected(files);
    },
    [onFilesSelected, schema],
  );

  const { isDragActive, error, dropzoneProps, inputProps, browse } = useFileDropzone({
    onFilesSelected: acceptSelection,
    accept: acceptedFileTypes,
    maxFileSize,
    maxFiles,
    describeError,
  });

  // The hook skips acceptSelection when a drop yields no files, which would leave
  // a stale schemaError — clear on every drop/change so both errors reset together.
  const clearing = useCallback(
    <E,>(handler: (event: E) => void) => (event: E) => {
      setSchemaError(null);
      handler(event);
    },
    [],
  );

  return (
    <div className="space-y-3">
      <button
        type="button"
        onClick={browse}
        {...dropzoneProps}
        onDrop={clearing(dropzoneProps.onDrop)}
        className={cn(
          'group flex min-h-[200px] w-full flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed p-8 text-center',
          'transition-[border-color,background-color] duration-150 ease-shell',
          'focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring',
          isDragActive
            ? 'border-primary bg-primary/10'
            : 'border-primary/25 bg-muted/40 hover:border-primary/50 hover:bg-muted/60',
        )}
      >
        <UploadCloud
          aria-hidden="true"
          strokeWidth={1.5}
          className={cn(
            'size-10 transition-transform duration-150 ease-shell motion-reduce:transition-none',
            isDragActive ? 'scale-110 text-primary' : 'text-primary/70 group-hover:scale-105',
          )}
        />

        <p className={cn('text-sm font-medium', isDragActive && 'text-primary')}>
          {isDragActive ? t('upload.dropFilesHere') : t('upload.dragDropFiles')}
        </p>
        <p className="text-xs text-muted-foreground">{t('upload.orClickToBrowse')}</p>

        <div className="mt-2 flex flex-wrap items-center justify-center gap-x-3 gap-y-1">
          {formatLabels.map((label) => (
            <span key={label} className="flex items-center gap-1 text-xs text-muted-foreground">
              <FileText aria-hidden="true" className="size-3.5" strokeWidth={1.8} />
              {label}
            </span>
          ))}
        </div>

        <p className="mt-1 text-xs revy-figure text-muted-foreground">
          {t('upload.maxSize', { size: `${Math.round(maxFileSize / 1024 / 1024)}MB` })}
          {' · '}
          {t('upload.maxFiles', { count: maxFiles })}
        </p>
      </button>

      <input
        {...inputProps}
        onChange={clearing(inputProps.onChange)}
        className="sr-only"
        tabIndex={-1}
        aria-hidden="true"
      />

      {/* Hook error wins: while set, nothing reached the schema, so schemaError is stale. */}
      {(error ?? schemaError) && (
        <Alert variant="destructive">
          <AlertDescription>{error ?? schemaError}</AlertDescription>
        </Alert>
      )}
    </div>
  );
};

export default DocumentUploadDropzone;
